import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";
import { filter, includes } from "lodash";

import { useTranslation } from "@root/misc/useTranslation";

import {
  LineupOption,
  LineupOptionID,
  LineupSlotInfoWithPickedOption,
  posForSlot,
  SlotKey,
} from "../types";

import { LineupContext } from "../context/LineupContext";
import { usePickerContext } from "../context/PickerContext";
import { useScoreDetailsContext } from "../context/ScoreDetailsContext";

import { useTeamImgProps } from "./teamImage";
import { StatsBlock, StatsLink } from "./Stats";

export interface SlotProps {
  layout: SlotLayout;
  markWarning: boolean;
  slot: SlotKey;
  canEdit: boolean;
  onPress?(): void;
  filling?: LineupSlotInfoWithPickedOption;
  censored?: boolean;
}

const OptionImage: React.FC<{
  option: LineupOption;
  markWarning?: boolean;
}> = ({ option, markWarning }) => {
  const className = [
    "neo__lineupSlot__imageCircle",
    "neo__lineupSlot__optionImage",
    markWarning ? "neo__lineupSlot__imageCircle--markWarning" : "",
  ].join(" ");

  return (
    <div className={className}>
      <div className="neo__lineupSlot__imageHolder">
        {option.image_url !== "" && (
          <img
            src={option.image_url}
            className={
              option.active ? undefined : "neo__lineupSlot__inactiveOptionImage"
            }
          />
        )}
      </div>
    </div>
  );
};

export const Score: React.FC<{
  score: number;
  size: "regular" | "small";
  markNotPartOfScoring?: boolean;
}> = ({ score, size, markNotPartOfScoring }) => {
  const className = [
    "neo__lineupSlot__score",
    `neo__lineupSlot__score--${size}`,
    markNotPartOfScoring
      ? "neo__lineupSlot__score--notPartOfScoring"
      : "neo__lineupSlot__score--partOfScoring",
  ].join(" ");

  return (
    <div className={className}>
      <strong>{score}</strong>
    </div>
  );
};

export const OptionImagePlaceholder: React.FC<{ kind: EmptyPlaceholderKind }> = ({
  kind,
}) => {
  const className = [
    "neo__lineupSlot__imageCircle",
    "neo__lineupSlot__imagePlaceholder",
    `neo__lineupSlot__imagePlaceholder--${kind}`,
  ].join(" ");

  return (
    <div className={className}>
      <div className="neo__lineupSlot__imageHolder">
        {kind === "censored" && (
          <div className="neo__lineupSlot__imageHolder__censoredInner" />
        )}
        {kind === "pick" && (
          <div className="neo__lineupSlot__imageHolder__pickInner" />
        )}
      </div>
    </div>
  );
};

export const optionStatsUrl = (optionId: LineupOptionID) => {
  return `https://www.penny-del.org/index.php?eID=neopolyRedirect&season=2026&player=${optionId}`;
};

type EmptyPlaceholderKind = "pick" | "none" | "censored";

const TextPlate: React.FC<
  PropsWithChildren<{
    topStart?: ReactNode;
    topEnd?: ReactNode;
    bottomContent?: ReactNode;
  }>
> = ({ children, topStart, topEnd, bottomContent }) => {
  return (
    <div className="neo__lineupSlot__textPlate">
      <div className="neo__lineupSlot__textPlate__topSection">
        <div className="neo__lineupSlot__textPlate__topSection__start">
          {topStart}
        </div>
        <div className="neo__lineupSlot__textPlate__topSection__end">
          {topEnd}
        </div>
      </div>
      <div className="neo__lineupSlot__textPlate__contentSection">
        {children}
      </div>
      {bottomContent && (
        <div className="neo__lineupSlot__bottomPlateContent">
          {bottomContent}
        </div>
      )}
    </div>
  );
};

const SlotInner: React.FC<
  SlotProps & {
    emptyPlaceholder: EmptyPlaceholderKind;
    bottomContent?: React.ReactNode;
  }
> = ({
  layout,
  slot,
  filling,
  emptyPlaceholder,
  markWarning,
  bottomContent,
}) => {
  const { t } = useTranslation();
  const teamImgProps = useTeamImgProps();
  const { open: openScoreDetailsPopup } = useScoreDetailsContext();
  const posLabel =
    slot === "goalsub"
      ? t("lineup.position.goalsub")
      : t(`lineup.position.${posForSlot(slot)}`);

  return (
    <div className="neo__lineupSlot__inner">
      {filling?.score_info && (
        <div className="neo__lineupSlot__inner__positionScore">
          <Score
            score={filling?.score_info?.score || 0}
            markNotPartOfScoring={!filling.score_info.part_of_scoring}
            size={layout === "regular" ? "regular" : "small"}
          />
        </div>
      )}
      <div className="neo__lineupSlot__inner__positionImage">
        {filling ? (
          <>
            <OptionImage
              option={filling.pickedOption}
              markWarning={markWarning}
            />
          </>
        ) : (
          <OptionImagePlaceholder kind={emptyPlaceholder} />
        )}
      </div>
      <div className="neo__lineupSlot__inner__positionScoreDetail">
        {filling?.score_info && (
          <div className="neo__lineupSlot__inner__infoIcon" onClick={ (e) => {
            e.stopPropagation();
            openScoreDetailsPopup(filling);
          } }/>
        )}
      </div>
      <div className="neo__lineupSlot__inner__positionText">
        <TextPlate
          topStart={
            filling && filling.pickedOption.price !== 0 ? (
              <div className="neo__lineupSlot__price">
                {filling.pickedOption.price}
                <div className="neo__lineupSlot__price__puk" />
              </div>
            ) : undefined
          }
          topEnd={<div className="neo__lineupSlot__pos">{posLabel}</div>}
          bottomContent={bottomContent}
        >
          {filling ? (
            <div
              className={
                "neo__lineupSlot__optionNameAndFlag" +
                (filling.pickedOption.german_license
                  ? " neo__lineupSlot__optionNameAndFlag--german"
                  : "")
              }
            >
              <span>
                <span
                  className={
                    filling.pickedOption.active
                      ? undefined
                      : "neo__lineupSlot__inactiveName"
                  }
                >
                  {filling.pickedOption.short_name || filling.pickedOption.name}
                </span>
                <span>{filling.pickedOption.country_abbr}</span>
              </span>
              {filling.pickedOption.image_url !== "" && (
                <img {...teamImgProps(filling.pickedOption.team)} />
              )}
            </div>
          ) : emptyPlaceholder === "censored" ? (
            <div className="neo__lineupSlot__emptyOptionLabel neo__lineupSlot__emptyOptionLabel--light">
              <span>{t("lineup.slot.label_censored")}</span>
            </div>
          ) : emptyPlaceholder === "none" ? (
            <div className="neo__lineupSlot__emptyOptionLabel neo__lineupSlot__emptyOptionLabel--light">
              <span>{t("lineup.slot.label_empty")}</span>
            </div>
          ) : (
            <div className="neo__lineupSlot__emptyOptionLabel neo__lineupSlot__emptyOptionLabel--strong">
              <span>{t("lineup.slot.label_please_pick")}</span>
            </div>
          )}
        </TextPlate>
      </div>
    </div>
  );
};

const EditableSlot: React.FC<SlotProps> = (props) => {
  return (
    <a className={slotClassName(props)} onClick={props.onPress}>
      <SlotInner
        emptyPlaceholder="pick"
        bottomContent={
          props.filling ? (
            <>
              <StatsBlock option={props.filling.pickedOption} />
              <StatsLink option={props.filling.pickedOption} />
            </>
          ) : undefined
        }
        {...props}
      />
    </a>
  );
};

const slotClassName = (props: SlotProps) => {
  return [
    "neo__lineupSlot",
    `neo__lineupSlot--${props.slot}`,
    `neo__lineupSlot--layout__${props.layout}`,
  ].join(" ");
};

export const useStatsLinkOnClick = (
  option: LineupOption
): React.MouseEventHandler<HTMLElement> => {
  return useCallback(
    (evt) => {
      window.open(optionStatsUrl(option.id));
      evt.stopPropagation();
    },
    [option.id]
  );
};

const NotEditableSlot: React.FC<SlotProps> = (props) => {
  return (
    <div className={slotClassName(props)}>
      <SlotInner
        emptyPlaceholder={props.censored ? "censored" : "none"}
        bottomContent={
          props.filling ? (
            <>
              <StatsBlock option={props.filling.pickedOption} />
              <StatsLink option={props.filling.pickedOption} />
            </>
          ) : undefined
        }
        {...props}
      />
    </div>
  );
};

export const Slot: React.FC<SlotProps> = (props) => {
  return props.canEdit ? (
    <EditableSlot {...props} />
  ) : (
    <NotEditableSlot {...props} />
  );
};

export type SlotLayout = "regular" | "smallAndInline";

export const SlotPlaceholder: React.FC<{ layout: SlotLayout }> = ({
  layout,
}) => {
  return (
    <div className={`neo__lineupSlot neo__lineupSlot--layout__${layout}`} />
  );
};

export const ConnectedSlot: React.FC<{ slot: SlotKey; layout: SlotLayout }> = ({
  slot,
  layout,
}) => {
  const {
    lineup,
    options,
    action,
    getSlotIfPicked,
    decideWhichActionsToSendForAPick,
  } = useContext(LineupContext);

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const { pick } = usePickerContext();

  const canEdit = lineup?.edit_info?.can_edit || false;

  const filledWith = getSlotIfPicked(slot);

  const onPress = useCallback(() => {
    if (!canEdit) {
      return;
    }

    const pos = posForSlot(slot);
    pick(
      slot,
      filter(optionsRef.current, (o) => o.position === pos && o.active)
    ).then((resp) => {
      if (resp.aborted) {
        return;
      }

      const actions = decideWhichActionsToSendForAPick(slot, resp.option?.id);
      if (actions) {
        action(actions);
      }
    });
  }, [canEdit, action, slot, pick]);

  return (
    <Slot
      canEdit={canEdit}
      onPress={onPress}
      layout={layout}
      slot={slot}
      censored={lineup?.censored}
      markWarning={
        !!(
          filledWith &&
          (!filledWith.pickedOption.active ||
            includes(lineup?.validity?.mark_invalid_slots || [], slot))
        )
      }
      filling={filledWith}
    />
  );
};
